import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Modal,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import {
  COLOR_PRIMARY,
  COLOR_PRIMARY_DARK,
  COLOR_PRIMARY_LIGHT,
  BACKGROUND_COLOR,
} from 'src/utils/const';
import { useUpdateCustomMarketName } from 'src/hooks/useCustomHooks';
import { useSelector } from 'react-redux';
import RateLimitAlert from '../../Components/Alerts/RateLimitAlert';

const styles = {
  modalContainer: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 'clamp(300px, 70vw, 500px)',
    bgcolor: 'white',
    boxShadow: 24,
    p: 4,
    borderRadius: '20px',
    maxHeight: '80vh',
    overflowY: 'auto',
  },
  closeButton: {
    position: 'absolute',
    top: '5px',
    right: '5px',
    cursor: 'pointer',
  },
  confirmButton: {
    background: COLOR_PRIMARY_DARK,
    borderRadius: 5,
    color: 'white',
    padding: '10px 15px',
    boxShadow: '0 1px 1px 1px rgba(255, 255, 255, .3)',
    '&:hover': {
      background: COLOR_PRIMARY_LIGHT,
    },
    width: '100%',
  },
  cancelButton: {
    background: 'white',
    borderRadius: 5,
    color: COLOR_PRIMARY_DARK,
    padding: '10px 15px',
    border: `1px solid ${COLOR_PRIMARY_DARK}`,
    '&:hover': {
      border: `1px solid ${COLOR_PRIMARY_LIGHT}`,
      color: COLOR_PRIMARY_LIGHT,
    },
    width: '100%',
  },
};

interface NameConfirmationModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  submarketName: string;
  setSubmarketName: (name: string) => void;
}

const NameConfirmationModal: React.FC<NameConfirmationModalProps> = ({
  open,
  onClose,
  onConfirm,
  submarketName,
  setSubmarketName,
}) => {
  const [isNameChanging, setIsNameChanging] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const {
    editableName,
    setEditableName,
    nameLoading,
    nameUpdateStatus,
    handleNameCheck,
    checkOrUpdateTailoredRegionName,
  } = useUpdateCustomMarketName(submarketName, setSubmarketName);

  const tailoredRegionCreationRateLimited = useSelector(
    (state: any) => state.dashboard.tailoredRegionCreationRateLimited
  );

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={styles.modalContainer}>
        <CloseIcon sx={styles.closeButton} onClick={onClose} />

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h5" sx={{ color: COLOR_PRIMARY, fontWeight: 500 }}>
              Submarket Created Successfully
            </Typography>
          </Grid>

          {tailoredRegionCreationRateLimited && (
            <Grid item xs={12}>
              <RateLimitAlert />
            </Grid>
          )}

          <Grid item xs={12}>
            {!tailoredRegionCreationRateLimited && (
              <Typography variant="body1" sx={{ fontWeight: 500, mb: 2 }}>
                Submarket Name - {submarketName}
                <Tooltip title="Edit">
                  <IconButton onClick={() => setIsEditing(true)}>
                    <EditIcon sx={{ fontSize: '1.2rem', color: COLOR_PRIMARY }} />
                  </IconButton>
                </Tooltip>
              </Typography>
            )}

            {isEditing && (
              <TextField
                variant="standard"
                fullWidth
                label="Enter Custom Name"
                value={editableName}
                onChange={handleNameCheck}
                error={nameUpdateStatus.error}
                helperText={nameLoading ? <CircularProgress size={20} /> : nameUpdateStatus.message}
                FormHelperTextProps={{
                  style: { color: nameUpdateStatus.error ? 'red' : 'green' },
                }}
              />
            )}
          </Grid>

          <Grid
            item
            xs={12}
            container
            spacing={2}
            justifyContent="space-between"
            direction="row-reverse"
          >
            {!isEditing && !tailoredRegionCreationRateLimited && (
              <Grid item>
                <Button variant="outlined" sx={styles.cancelButton} onClick={onClose}>
                  Go to Submarket
                </Button>
              </Grid>
            )}
            {isEditing && !tailoredRegionCreationRateLimited && (
              <Grid item>
                <Button
                  variant="contained"
                  sx={styles.confirmButton}
                  onClick={async () => {
                    if (isNameChanging) return;
                    setIsNameChanging(true);
                    await checkOrUpdateTailoredRegionName('rename', editableName);
                    setIsNameChanging(false);
                    onClose();
                  }}
                >
                  {isNameChanging ? <CircularProgress size={20} /> : 'Update Name'}
                </Button>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Box>
    </Modal>
  );
};

export default NameConfirmationModal;
